/**
 * Product analytics, behind consent and behind a redactor.
 *
 * Two rules, and the second one is the reason this file exists rather than a
 * one-line `getAnalytics(app)` in the Firebase setup:
 *
 *   1. **Nothing loads until consent is granted.** Not the SDK, not a cookie,
 *      not a network request. Declining is not "load it and tell it to behave"
 *      — it is never fetching it at all.
 *
 *   2. **No URL is ever sent as written.** Signing, embed and completed-copy
 *      URLs carry a bearer token: anyone holding one can open the agreement.
 *      GA4's automatic page-view collection would send the full path, which
 *      would hand those tokens to a third party and write them into somebody
 *      else's logs. So automatic collection is switched off, and page views
 *      are reported manually as a route *pattern* — `/blog/:slug`, never
 *      `/blog/what-makes-an-audit-trail-worth-anything`, and nothing at all
 *      for a route that cannot be matched.
 *
 * The same reasoning applies to tenant identifiers: an envelope id in a URL is
 * not a token, but it is somebody's data, and it has no business leaving this
 * system to be measured.
 */
import { authConfig } from './firebase';

/** Routes that must never be measured, because their URL is a credential. */
const FORBIDDEN = ['/sign/', '/embed/', '/completed/', '/dev/'];

/** Every path the product can show, as a pattern. Anything not listed is not
 *  reported — an allow-list, because the failure mode of a deny-list here is
 *  leaking a token the day somebody adds a route. */
const PATTERNS: [RegExp, string][] = [
  [/^\/$/, '/'],
  [/^\/pricing\/?$/, '/pricing'],
  [/^\/blog\/?$/, '/blog'],
  [/^\/blog\/[^/]+\/?$/, '/blog/:slug'],
  [/^\/terms\/?$/, '/terms'],
  [/^\/privacy\/?$/, '/privacy'],
  [/^\/support\/?$/, '/support'],
  [/^\/contact\/?$/, '/contact'],
  [/^\/verify\/?$/, '/verify'],
  [/^\/prepare\/?$/, '/prepare'],
  [/^\/login\/?$/, '/login'],
  [/^\/register\/?$/, '/register'],
  [/^\/app\/?$/, '/app'],
  [/^\/app\/new\/?$/, '/app/new'],
  [/^\/app\/envelopes\/[^/]+\/?$/, '/app/envelopes/:id'],
  [/^\/app\/templates\/?$/, '/app/templates'],
  [/^\/app\/billing\/?$/, '/app/billing'],
  [/^\/app\/developers\/?$/, '/app/developers'],
  [/^\/app\/settings\/?$/, '/app/settings'],
];

/** Whether this page may be measured at all. */
export function measurable(pathname: string): boolean {
  if (FORBIDDEN.some((prefix) => pathname.startsWith(prefix))) return false;
  return PATTERNS.some(([pattern]) => pattern.test(pathname));
}

/** The route pattern to report, or null if this path is not reportable. */
export function safePath(pathname: string): string | null {
  if (FORBIDDEN.some((prefix) => pathname.startsWith(prefix))) return null;
  const match = PATTERNS.find(([pattern]) => pattern.test(pathname));
  return match ? match[1] : null;
}

type Analytics = import('firebase/analytics').Analytics;

let started: Promise<Analytics | null> | null = null;
let instance: Analytics | null = null;
let logEventFn: typeof import('firebase/analytics').logEvent | null = null;

/** Load and initialize analytics. Only ever called after consent. */
export function startAnalytics(): Promise<Analytics | null> {
  if (!started) {
    started = (async () => {
      const config = (await authConfig()).firebase;
      const analytics = config.analytics;
      if (!analytics?.enabled || !config.api_key || !analytics.measurement_id) {
        return null;
      }
      const [{ initializeApp, getApps }, mod] = await Promise.all([
        import('firebase/app'),
        import('firebase/analytics'),
      ]);
      if (!(await mod.isSupported())) return null;
      const app = getApps()[0] ?? initializeApp({
        apiKey: config.api_key,
        authDomain: config.auth_domain,
        projectId: config.project_id,
        appId: analytics.app_id,
        measurementId: analytics.measurement_id,
      });
      instance = mod.initializeAnalytics(app, {
        config: {
          // We report page views ourselves, as patterns. Automatic collection
          // would send the real URL, and some of those URLs are credentials.
          send_page_view: false,
          // No advertising identifiers and no cross-site signals: this is
          // product measurement, and the Privacy Policy says we do not do the
          // other thing.
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
          anonymize_ip: true,
        },
      });
      logEventFn = mod.logEvent;
      return instance;
    })().catch(() => null);
  }
  return started;
}

/** Forget everything, so a withdrawal of consent takes effect immediately. */
export function stopAnalytics(): void {
  started = null;
  instance = null;
  logEventFn = null;
}

/** Report one page view, as a pattern, if this page may be measured. */
export async function trackPage(pathname: string): Promise<void> {
  const path = safePath(pathname);
  if (!path) return;
  await startAnalytics();
  if (!instance || !logEventFn) return;
  logEventFn(instance, 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
  });
}
